import { extractApiError, type HttpApiClient } from "@vana/core";
import { fetchMaxAttempts } from "../../../../shared/delivery/max-attempts";
import { fetchZoneOptions } from "../../../../shared/geo/coverage-repo";
import { type ZoneOption } from "../../../../shared/geo/zone-option";
import { type RegisteredAddress, type RegisteredAddressType } from "../../domain/entities/registered-address";
import {
  GetMaxAttemptsError,
  MoveToUndeliverableError,
  RejectByCustomerError,
  FailAttemptError,
  ListAddressesError,
  ListZoneOptionsError,
  RescheduleError,
  StartDeliveryError,
  UploadEvidenceError,
} from "../../domain/errors/errors";
import {
  type MoveToUndeliverableParams,
  type RejectByCustomerParams,
  type FailAttemptParams,
  type FailedDeliveryRepository,
  type ListAddressesParams,
  type RescheduleParams,
  type RescheduleToPoolParams,
  type StartParams,
  type UploadEvidenceParams,
} from "../../domain/repos/failed-delivery-repo";

// Capture type for the evidence backing a failed attempt (matches the backend).
const EVIDENCE_CAPTURE_TYPE = "failed_attempt_evidence";

// Snake_cased response is camelCased by the client interceptor.
interface AddressItemResponse {
  addressId: string;
  type: string;
  text: string;
  isCurrent: boolean;
  hasDeliveryTarget?: boolean;
}
interface AddressesResponse {
  data?: { items?: AddressItemResponse[] };
}

export class FailedDeliveryRepo implements FailedDeliveryRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  // The request interceptor snake_cases bodies (deliverySlotAt ->
  // delivery_slot_at). The acting ambassador is the session user (BFF).
  async start({ requestId, lat, long }: StartParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${requestId}/start`, { lat, long });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new StartDeliveryError(message, apiError);
    }
  }

  async failAttempt({ requestId, reason, reasonCode, lat, long }: FailAttemptParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${requestId}/failed-attempt`, {
        reason,
        reasonCode,
        lat,
        long,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new FailAttemptError(message, apiError);
    }
  }

  async reschedule({
    requestId,
    reason,
    deliveryDate,
    deliveryShift,
    addressId,
    newAddress,
    lat,
    long,
  }: RescheduleParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${requestId}/reschedule`, {
        reason,
        deliveryDate,
        deliveryShift,
        addressId,
        newAddress,
        lat,
        long,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RescheduleError(message, apiError);
    }
  }

  async rescheduleToPool({
    requestId,
    reason,
    deliveryDate,
    deliveryShift,
    addressId,
    newAddress,
    lat,
    long,
  }: RescheduleToPoolParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${requestId}/reschedule-to-pool`, {
        reason,
        deliveryDate,
        deliveryShift,
        addressId,
        newAddress,
        lat,
        long,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RescheduleError(message, apiError);
    }
  }

  async moveToUndeliverable({
    requestId,
    reason,
    reasonCode,
    lat,
    long,
  }: MoveToUndeliverableParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${requestId}/undeliverable`, {
        reason,
        reasonCode,
        lat,
        long,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new MoveToUndeliverableError(message, apiError);
    }
  }

  async rejectByCustomer({
    requestId,
    note,
    lat,
    long,
  }: RejectByCustomerParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${requestId}/rejection`, {
        note,
        lat,
        long,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RejectByCustomerError(message, apiError);
    }
  }

  async listAddresses({ requestId }: ListAddressesParams): Promise<RegisteredAddress[]> {
    try {
      const response = await this.apiClient.get<AddressesResponse>(`v1/requests/${requestId}/addresses`);
      return (response.data?.items ?? []).map((r) => ({
        addressId: r.addressId,
        type: r.type as RegisteredAddressType,
        text: r.text,
        isCurrent: r.isCurrent,
        // A backend that predates the flag routes every address as before.
        hasDeliveryTarget: r.hasDeliveryTarget ?? true,
      }));
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAddressesError(message, apiError);
    }
  }

  async listZoneOptions(): Promise<ZoneOption[]> {
    try {
      return await fetchZoneOptions(this.apiClient);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListZoneOptionsError(message, apiError);
    }
  }

  async getMaxAttempts(): Promise<number> {
    try {
      return await fetchMaxAttempts(this.apiClient);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetMaxAttemptsError(message, apiError);
    }
  }

  async uploadEvidence({ requestId, image }: UploadEvidenceParams): Promise<void> {
    try {
      const form = new FormData();
      form.append("type", EVIDENCE_CAPTURE_TYPE);
      form.append("file", image, `${EVIDENCE_CAPTURE_TYPE}.jpg`);
      await this.apiClient.post(`v1/requests/${requestId}/captures`, form);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new UploadEvidenceError(message, apiError);
    }
  }
}
